<script setup lang="ts">
import { nextTick, onMounted, onUnmounted, ref, watch } from 'vue'

const props = defineProps<{
  open: boolean
  title: string
  body: string
  confirmLabel: string
  loading?: boolean
  danger?: boolean
  // Keeps the Confirm button disabled independently of `loading` — used by
  // flows that require extra client-side conditions before submitting (e.g.
  // a typed-confirmation echo of a pricing value).
  disableConfirm?: boolean
}>()

const emit = defineEmits<{
  'update:open': [value: boolean]
  confirm: []
}>()

const titleId = 'confirm-dialog-title'
const bodyId = 'confirm-dialog-body'

const confirmButtonRef = ref<HTMLButtonElement | null>(null)

function close(): void {
  emit('update:open', false)
}

function handleOverlayClick(): void {
  if (!props.loading) {
    close()
  }
}

function handleConfirm(): void {
  emit('confirm')
}

function handleKeyDown(event: KeyboardEvent): void {
  if (event.key === 'Escape' && !props.loading) {
    close()
  }
}

watch(
  () => props.open,
  async (isOpen) => {
    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown)
      await nextTick()
      confirmButtonRef.value?.focus()
    } else {
      document.removeEventListener('keydown', handleKeyDown)
    }
  },
)

onMounted(() => {
  if (props.open) {
    document.addEventListener('keydown', handleKeyDown)
    void nextTick(() => {
      confirmButtonRef.value?.focus()
    })
  }
})

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeyDown)
})
</script>

<template>
  <div
    v-if="open"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
    role="alertdialog"
    aria-modal="true"
    :aria-labelledby="titleId"
    :aria-describedby="bodyId"
    @click.self="handleOverlayClick"
  >
    <div class="bg-slate-800 border border-slate-700 rounded-2xl p-6 max-w-md w-full mx-4 shadow-2xl">
      <h2 :id="titleId" class="text-lg font-bold text-white mb-2">{{ title }}</h2>
      <p :id="bodyId" class="text-sm text-slate-400" :class="$slots.default || $slots.warning ? 'mb-3' : 'mb-6'">{{ body }}</p>
      <!-- Optional extra content (e.g. a before/after diff, retype-to-confirm inputs) -->
      <div v-if="$slots.default" class="mb-3">
        <slot />
      </div>
      <div v-if="$slots.warning" class="rounded-lg bg-amber-500/10 border border-amber-500/30 px-4 py-3 text-sm text-amber-300 mb-6">
        <slot name="warning" />
      </div>

      <div class="flex items-center gap-3 justify-end">
        <button
          type="button"
          :disabled="loading"
          class="border border-slate-600 hover:border-slate-500 text-slate-300 hover:text-white font-semibold rounded-lg px-4 py-2 text-sm transition-colors disabled:opacity-50"
          @click="close"
        >
          <slot name="cancel-label">{{ $t('admin.common.cancel') }}</slot>
        </button>
        <button
          ref="confirmButtonRef"
          type="button"
          :disabled="loading || disableConfirm"
          class="font-semibold rounded-lg px-4 py-2 text-sm transition-colors flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          :class="danger !== false
            ? 'bg-red-500 hover:bg-red-400 text-white'
            : 'bg-slate-600 hover:bg-slate-500 text-white'"
          @click="handleConfirm"
        >
          <svg v-if="loading" class="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4" />
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
          {{ confirmLabel }}
        </button>
      </div>
    </div>
  </div>
</template>
